/**
 * CASCATA LOCAL DE CONTEXTO: CLIENTE → PROJETO → UNIDADE / ÁREA → EQUIPAMENTO.
 *
 * O cabeçalho global não tem mais Cliente: cada tela operacional escolhe o
 * seu. Esta é a regra única da cascata, usada pelos filtros das listagens e
 * pelos formulários de cadastro (Instrumentos, PSV, Frentes de Serviço):
 *
 *   - trocar um nível limpa TODOS os níveis abaixo dele — nunca sobra
 *     "Cliente B + Projeto de A";
 *   - cada nível só oferece opções compatíveis com os de cima.
 *
 * Funções puras: a tela desenha, a regra fica aqui e é testada sem DOM.
 */

export interface CascadeSelection {
  readonly clientId: string;
  readonly projectId: string;
  readonly areaId: string;
  readonly equipmentId: string;
}

export type CascadeLevel = keyof CascadeSelection;

const LEVELS: readonly CascadeLevel[] = ["clientId", "projectId", "areaId", "equipmentId"];

export const EMPTY_CASCADE: CascadeSelection = { clientId: "", projectId: "", areaId: "", equipmentId: "" };

/** Define um nível e limpa todos os que dependem dele. */
export function changeCascade(selection: CascadeSelection, level: CascadeLevel, value: string): CascadeSelection {
  const index = LEVELS.indexOf(level);
  const next: Record<CascadeLevel, string> = { ...selection };
  next[level] = value;
  for (const child of LEVELS.slice(index + 1)) next[child] = "";
  return next;
}

export interface CascadeProject { id: string; clientId: string }
export interface CascadeArea { id: string; clientId: string; projectId: string }
export interface CascadeEquipment {
  id: string;
  clientId: string;
  projectId?: string | null;
  unitAreaId?: string | null;
  areaId?: string | null;
}

/** "Todos os Clientes" (vazio) mantém só os Clientes autorizados — a lista `authorizedClientIds`. */
export function projectsForCascade<T extends CascadeProject>(
  projects: readonly T[],
  selection: Pick<CascadeSelection, "clientId">,
  authorizedClientIds: ReadonlySet<string>
): T[] {
  return projects.filter(p =>
    selection.clientId ? String(p.clientId) === selection.clientId : authorizedClientIds.has(String(p.clientId))
  );
}

export function areasForCascade<T extends CascadeArea>(
  areas: readonly T[],
  selection: Pick<CascadeSelection, "clientId" | "projectId">,
  authorizedClientIds: ReadonlySet<string>
): T[] {
  return areas.filter(a => {
    if (selection.clientId ? String(a.clientId) !== selection.clientId : !authorizedClientIds.has(String(a.clientId))) return false;
    return !selection.projectId || String(a.projectId) === selection.projectId;
  });
}

/** Unidade/Área do equipamento — `unitAreaId` no cadastro atual, `areaId` nos antigos. */
function areaOf(equipment: CascadeEquipment): string {
  return String(equipment.unitAreaId ?? "").trim() || String(equipment.areaId ?? "").trim();
}

export function equipmentsForCascade<T extends CascadeEquipment>(
  equipments: readonly T[],
  selection: Pick<CascadeSelection, "clientId" | "projectId" | "areaId">
): T[] {
  return equipments.filter(e => {
    if (selection.clientId && String(e.clientId) !== selection.clientId) return false;
    if (selection.projectId && String(e.projectId ?? "") !== selection.projectId) return false;
    if (selection.areaId && areaOf(e) !== selection.areaId) return false;
    return true;
  });
}

/**
 * RESPOSTA ATRASADA NÃO SOBRESCREVE A ATUAL.
 *
 * Cliente A → (consulta A) → Cliente B → (consulta B) → A responde por
 * último: sem esta trava, a tela mostraria A com o filtro em B. Cada
 * consulta pega um bilhete; só o bilhete mais recente pode desenhar.
 */
export class LatestRequestGate {
  private generation = 0;

  begin(): number {
    this.generation += 1;
    return this.generation;
  }

  isCurrent(ticket: number): boolean {
    return ticket === this.generation;
  }
}
